import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArmchairIcon,
  BuildingsIcon,
  CompassIcon,
  LampPendantIcon,
  PlugsConnectedIcon,
  SolarPanelIcon,
  SquaresFourIcon,
  StorefrontIcon,
  UsersThreeIcon,
} from "@phosphor-icons/react";

/**
 * Explore-category → Phosphor icon + subtitle map. Keyword-matched so naming
 * variants ("Interior Designing" / "Interior Designers") resolve to the same
 * icon. Ported from the mobile app's ExploreCategoriesCard so the web
 * requirement flow shows the exact same 3-up card grid.
 */
const EXPLORE_CATEGORY_ICONS = [
  { match: /interior/i, Icon: UsersThreeIcon, subtitle: "Design your dream space" },
  { match: /architect/i, Icon: BuildingsIcon, subtitle: "Plan your dream home" },
  { match: /vastu/i, Icon: CompassIcon, subtitle: "Align energy & spaces" },
  { match: /furnitur|decor/i, Icon: ArmchairIcon, subtitle: "Style every corner of your home" },
  { match: /automat|smart\s*home/i, Icon: PlugsConnectedIcon, subtitle: "Automate your home" },
  { match: /light/i, Icon: LampPendantIcon, subtitle: "Illuminate perfectly" },
  { match: /solar|sustain/i, Icon: SolarPanelIcon, subtitle: "Go green & save" },
  { match: /brand|store|shop/i, Icon: StorefrontIcon, subtitle: "Discover brands" },
];

const DEFAULT_EXPLORE_CATEGORY_ICON = {
  Icon: SquaresFourIcon,
  subtitle: "Explore professionals",
};

/**
 * Colour palette for category icon squares — soft tinted background paired with
 * a saturated icon colour. Every category is mapped into this palette by
 * hashing its name, so the colour is "random-looking" yet STABLE: the same
 * category always renders the same tone, and adjacent cards read as varied.
 */
const EXPLORE_CATEGORY_PALETTE = [
  { bg: "#eef3fb", color: "#315f8f" }, // blue
  { bg: "#f4f0ea", color: "#7b5a31" }, // brown
  { bg: "#f8f0ec", color: "#9a5436" }, // terracotta
  { bg: "#f2f5ec", color: "#637a32" }, // olive
  { bg: "#eef5f7", color: "#2d6874" }, // teal
  { bg: "#f8f4df", color: "#927317" }, // gold
  { bg: "#eaf6f2", color: "#2f7568" }, // sea green
  { bg: "#eaf7ef", color: "#267449" }, // emerald
  { bg: "#f3eefb", color: "#5b3f8f" }, // purple
  { bg: "#fbeef4", color: "#8f3f6a" }, // magenta
  { bg: "#fbf0ee", color: "#a5442f" }, // clay red
  { bg: "#eef0fb", color: "#3f4a8f" }, // indigo
  { bg: "#f0f8ee", color: "#3f7a3f" }, // leaf
  { bg: "#fdf3e9", color: "#b56a1f" }, // orange
  { bg: "#eafaf6", color: "#1f8f74" }, // mint
  { bg: "#f7eefb", color: "#7a3f8f" }, // violet
];

/** Stable, order-independent string hash (djb2-ish) → non-negative int. */
function hashCategoryName(text) {
  let h = 0;
  for (let i = 0; i < text.length; i += 1) {
    h = (h * 31 + text.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

export function getExploreCategoryTone(name) {
  const text = String(name ?? "").trim();
  const iconHit =
    EXPLORE_CATEGORY_ICONS.find((entry) => entry.match.test(text)) ??
    DEFAULT_EXPLORE_CATEGORY_ICON;
  // Hash on the lower-cased name so casing/whitespace variants land on the same
  // colour, then index into the palette. Empty names fall on index 0.
  const palette =
    EXPLORE_CATEGORY_PALETTE[
      hashCategoryName(text.toLowerCase()) % EXPLORE_CATEGORY_PALETTE.length
    ];
  return {
    Icon: iconHit.Icon,
    subtitle: iconHit.subtitle,
    bg: palette.bg,
    color: palette.color,
  };
}

/** True when the icon is a real category image, not the generic fallback. */
function hasRealImage(icon) {
  const value = String(icon || "").trim();
  return value !== "" && value !== "/Category.svg";
}

function CategorySelectionScreen({
  categories,
  isLoadingCategories,
  isLoadingQuestions,
  categoriesError,
  questionsError,
  noQuestionsFound,
  selectedCategoryId,
  onSelect,
  onContinue,
}) {
  const navigate = useNavigate();
  const shouldShowNoQuestionsCta = Boolean(noQuestionsFound) && !isLoadingQuestions;
  const autoNavTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (autoNavTimerRef.current) {
        clearTimeout(autoNavTimerRef.current);
      }
    };
  }, []);

  const handleCategoryClick = (category) => {
    if (isLoadingCategories || isLoadingQuestions) return;
    onSelect(category);
    if (autoNavTimerRef.current) {
      clearTimeout(autoNavTimerRef.current);
    }
    // Automatically navigate to the next step (questions)
    autoNavTimerRef.current = setTimeout(() => {
      onContinue(category);
    }, 200);
  };

  const handleManualContinue = () => {
    if (autoNavTimerRef.current) {
      clearTimeout(autoNavTimerRef.current);
    }
    if (shouldShowNoQuestionsCta) {
      navigate("/");
    } else {
      onContinue();
    }
  };

  return (
    <div className="req-screen">
      <div className="req-content">
        <div className="req-inline-header">
          <button
            type="button"
            className="req-back-icon-btn"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <img src="/caretLeft.svg" alt="" />
          </button>
          <p className="req-eyebrow">Post your requirement</p>
        </div>

        <h1 className="req-heading">What do you need help with?</h1>

        {isLoadingCategories ? (
          <div className="req-category-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={`cat-skeleton-${i}`} className="req-category-card is-skeleton">
                <span className="req-cat-skel-icon" />
                <span className="req-cat-skel-text">
                  <span className="req-cat-skel-line wide" />
                  <span className="req-cat-skel-line narrow" />
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="req-category-grid">
            {categories.map((category) => {
              const selected = category.id === selectedCategoryId;
              const tone = getExploreCategoryTone(category.label);
              const CategoryIcon = tone.Icon;
              const description =
                String(category.description || "").trim() || tone.subtitle;
              const showImage = hasRealImage(category.icon);
              return (
                <button
                  key={category.id}
                  type="button"
                  className={`req-category-card ${selected ? "is-selected" : ""}`}
                  onClick={() => handleCategoryClick(category)}
                >
                  <span
                    className="req-category-icon-wrap"
                    style={{ backgroundColor: tone.bg }}
                  >
                    {showImage ? (
                      <img
                        src={category.icon}
                        alt=""
                        className="req-category-icon"
                      />
                    ) : (
                      <CategoryIcon size={24} color={tone.color} weight="fill" />
                    )}
                  </span>
                  <span className="req-category-text">
                    <span className="req-category-label">{category.label}</span>
                    <span className="req-category-desc">{description}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
        {categoriesError && <p className="req-select-hint">{categoriesError}</p>}
        {questionsError && <p className="req-select-hint">{questionsError}</p>}
      </div>

      <div className="req-footer">
        <div className="req-progress-track">
          <div className="req-progress-fill req-progress-fill-early" />
        </div>
        <button
          type="button"
          className="req-primary-btn req-primary-btn-full"
          onClick={handleManualContinue}
          disabled={
            shouldShowNoQuestionsCta
              ? false
              : !selectedCategoryId || isLoadingCategories || isLoadingQuestions
          }
        >
          {isLoadingQuestions
            ? "Loading..."
            : shouldShowNoQuestionsCta
              ? "Continue to Home"
              : "Continue"}
        </button>
      </div>
    </div>
  );
}

export default CategorySelectionScreen;
